import { TimelineEvent, VisibleSkills, ResourceConfig } from '../consts/viewTypes';
import { OverrideStore } from '../consts/overrideTypes';
import { OperatorLoadoutState } from '../view/OperatorLoadoutHeader';
import { LoadoutProperties } from '../view/InformationPane';
import { EnemyStats } from '../controller/appStateController';
import { LoadoutTree } from './loadoutStorage';
import { NounType } from '../dsl/semantics';

const STORAGE_KEY = 'zst-sheet';
const CURRENT_VERSION = 3;

export interface SheetData {
  version: number;
  operatorIds: (string | null)[];
  enemyId: string;
  enemyStats?: EnemyStats;
  events: TimelineEvent[];
  loadouts: Record<string, OperatorLoadoutState>;
  loadoutProperties: Record<string, LoadoutProperties>;
  visibleSkills: VisibleSkills;
  nextEventId: number;
  resourceConfigs?: Record<string, ResourceConfig>;
  overrides?: OverrideStore;
}

export function serializeSheet(
  operatorIds: (string | null)[],
  enemyId: string,
  enemyStats: EnemyStats | undefined,
  events: TimelineEvent[],
  loadouts: Record<string, OperatorLoadoutState>,
  loadoutProperties: Record<string, LoadoutProperties>,
  visibleSkills: VisibleSkills,
  nextEventId: number,
  resourceConfigs?: Record<string, ResourceConfig>,
  overrides?: OverrideStore,
): SheetData {
  return {
    version: CURRENT_VERSION,
    operatorIds,
    enemyId,
    ...(enemyStats ? { enemyStats } : {}),
    events,
    loadouts,
    loadoutProperties,
    visibleSkills,
    nextEventId,
    ...(resourceConfigs && Object.keys(resourceConfigs).length > 0 ? { resourceConfigs } : {}),
    ...(overrides && Object.keys(overrides).length > 0 ? { overrides } : {}),
  };
}

export type LoadResult =
  | { ok: true; data: SheetData }
  | { ok: false; error: string };

/** Migrate sheet data from older versions. */
function migrateSheetData(data: Record<string, unknown>): SheetData {
  // Migrate legacy lowercase '-ultimate' resource config keys to NounType.ULTIMATE
  if (data.resourceConfigs) {
    const rc = data.resourceConfigs as Record<string, ResourceConfig>;
    const migrated: Record<string, ResourceConfig> = {};
    for (const [key, val] of Object.entries(rc)) {
      const migratedKey = key.replace(/-ultimate$/, `-${NounType.ULTIMATE}`);
      migrated[migratedKey] = val;
    }
    data.resourceConfigs = migrated;
  }
  return data as unknown as SheetData;
}

export function validateSheetData(raw: unknown): LoadResult {
  if (raw == null || typeof raw !== 'object') {
    return { ok: false, error: 'Save data is not a valid object.' };
  }
  const migrated = migrateSheetData(raw as Record<string, unknown>);
  return { ok: true, data: migrated };
}

// ─── Clean & normalize ───────────────────────────────────────────────────

/** Stamp current version and strip transient derived fields before persisting.
 *  Segments are preserved — user edits to segment durations and frame offsets
 *  live on the segments array and must survive save/load round-trips.
 *  Unedited events never have segments on the raw event (they are attached
 *  from column templates at display time by attachDefaultSegments).
 */
export function cleanSheetData(data: SheetData): SheetData {
  return { ...data, version: CURRENT_VERSION };
}

// ─── LocalStorage ────────────────────────────────────────────────────────

export function saveToLocalStorage(data: SheetData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cleanSheetData(data)));
  } catch {
    // Storage full or unavailable — silently fail
  }
}

export function loadFromLocalStorage(): LoadResult | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw == null) return null;
    const parsed = JSON.parse(raw);
    return validateSheetData(parsed);
  } catch (e) {
    return { ok: false, error: `Failed to parse saved data: ${e instanceof Error ? e.message : String(e)}` };
  }
}

export function clearLocalStorage(): void {
  localStorage.removeItem(STORAGE_KEY);
}

// ─── File export/import ──────────────────────────────────────────────────

export function exportToFile(data: SheetData): void {
  const json = JSON.stringify(cleanSheetData(data), null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'endfield-timeline.json';
  a.click();
  URL.revokeObjectURL(url);
}

export function importFromFile(): Promise<LoadResult> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) {
        resolve({ ok: false, error: 'No file selected.' });
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = JSON.parse(reader.result as string);
          resolve(validateSheetData(parsed));
        } catch (e) {
          resolve({ ok: false, error: `Failed to parse file: ${e instanceof Error ? e.message : String(e)}` });
        }
      };
      reader.onerror = () => {
        resolve({ ok: false, error: 'Failed to read file.' });
      };
      reader.readAsText(file);
    };
    input.click();
  });
}

// ─── Multi-loadout bundle export/import ──────────────────────────────────

const BUNDLE_VERSION = 1;

export interface MultiLoadoutBundle {
  bundleVersion: number;
  tree: LoadoutTree;
  loadouts: Record<string, SheetData>;
}

export type BundleLoadResult =
  | { ok: true; data: MultiLoadoutBundle }
  | { ok: false; error: string };

export function exportMultiLoadoutBundle(
  tree: LoadoutTree,
  selectedLoadoutIds: Set<string>,
  getLoadoutData: (id: string) => SheetData | null,
): void {
  // Collect ancestor folder IDs for selected loadouts to preserve structure
  const selectedArr = Array.from(selectedLoadoutIds);
  const includedIds = new Set<string>(selectedArr);
  for (const sid of selectedArr) {
    let node = tree.nodes.find((n) => n.id === sid);
    while (node?.parentId) {
      includedIds.add(node.parentId);
      const parentId = node.parentId;
      node = tree.nodes.find((n) => n.id === parentId);
    }
  }

  const filteredTree: LoadoutTree = {
    nodes: tree.nodes.filter((n) => includedIds.has(n.id)),
  };

  const loadouts: Record<string, SheetData> = {};
  for (const sid of selectedArr) {
    const data = getLoadoutData(sid);
    if (data) loadouts[sid] = cleanSheetData(data);
  }

  const bundle: MultiLoadoutBundle = {
    bundleVersion: BUNDLE_VERSION,
    tree: filteredTree,
    loadouts,
  };

  const json = JSON.stringify(bundle, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = buildBundleFilename(selectedArr, tree);
  a.click();
  URL.revokeObjectURL(url);
}

function buildBundleFilename(selectedLoadoutIds: string[], tree: LoadoutTree): string {
  const ts = formatLocalTimestamp(new Date());
  if (selectedLoadoutIds.length === 1) {
    const node = tree.nodes.find((n) => n.id === selectedLoadoutIds[0]);
    if (node) return `${sanitizeFilename(node.name)}-${ts}.json`;
  }
  return `zero-sanity-simulations-${ts}.json`;
}

function sanitizeFilename(name: string): string {
  const cleaned = name.replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, ' ').trim();
  return cleaned || 'loadout';
}

function formatLocalTimestamp(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}-${pad(date.getMinutes())}-${pad(date.getSeconds())}`;
}

export function validateMultiLoadoutBundle(raw: unknown): BundleLoadResult {
  if (raw == null || typeof raw !== 'object') {
    return { ok: false, error: 'Bundle is not a valid object.' };
  }
  const obj = raw as Record<string, unknown>;

  if (typeof obj.bundleVersion !== 'number') {
    return { ok: false, error: 'Missing or invalid bundleVersion field.' };
  }
  if (obj.bundleVersion > BUNDLE_VERSION) {
    return { ok: false, error: `Bundle version (${obj.bundleVersion}) is newer than this app supports (${BUNDLE_VERSION}).` };
  }

  const tree = obj.tree as Record<string, unknown> | undefined;
  if (!tree || !Array.isArray(tree.nodes)) {
    return { ok: false, error: 'Missing or invalid tree field.' };
  }
  for (const node of tree.nodes as unknown[]) {
    const n = node as Record<string, unknown>;
    if (!n || typeof n.id !== 'string' || typeof n.name !== 'string' || typeof n.type !== 'string') {
      return { ok: false, error: 'Invalid node in tree.' };
    }
  }

  const loadoutsObj = obj.loadouts as Record<string, unknown> | undefined;
  if (typeof loadoutsObj !== 'object' || loadoutsObj == null) {
    return { ok: false, error: 'Missing or invalid loadouts field.' };
  }
  for (const [id, loadoutData] of Object.entries(loadoutsObj)) {
    const result = validateSheetData(loadoutData);
    if (!result.ok) {
      return { ok: false, error: `Invalid loadout data for "${id}": ${result.error}` };
    }
  }

  return { ok: true, data: obj as unknown as MultiLoadoutBundle };
}

export function importMultiLoadoutFile(): Promise<BundleLoadResult> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) {
        resolve({ ok: false, error: 'No file selected.' });
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = JSON.parse(reader.result as string);
          resolve(validateMultiLoadoutBundle(parsed));
        } catch (e) {
          resolve({ ok: false, error: `Failed to parse file: ${e instanceof Error ? e.message : String(e)}` });
        }
      };
      reader.onerror = () => {
        resolve({ ok: false, error: 'Failed to read file.' });
      };
      reader.readAsText(file);
    };
    input.click();
  });
}
